import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import { canAccessSalesDataImport } from "../features/salesDataImport/access";
import SalesImportNav from "../features/salesDataImport/components/SalesImportNav";
import { salesImportBranchDisplayName } from "../features/salesDataImport/config/salesImportBranchIds";
import { useSalesImportBranchFilter } from "../features/salesDataImport/hooks/useSalesImportBranchFilter";
import {
  generateAdvancedSalesReport,
  generateAdvancedSalesReportFromRows,
} from "../features/salesDataImport/services/advancedReportsService";
import { exportAdvancedSalesReportExcel } from "../features/salesDataImport/services/advancedReportsExportService";
import {
  listUploadedWeekPeriods,
  loadWeeklyLineChunkRows,
} from "../features/salesDataImport/services/salesLineChunksReader";
import "./SalesDataImportCenter.css";

function fm(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function pct(v) {
  if (v == null || Number.isNaN(v)) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
}

export default function SalesAdvancedReportsPage() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch {
      return {};
    }
  }, []);

  const [dark, setDark] = useState(() => localStorage.getItem("sdic-dark") === "1");
  const [reportMode, setReportMode] = useState("week");
  const [uploadedWeeks, setUploadedWeeks] = useState([]);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState("");
  const [weekRows, setWeekRows] = useState([]);
  const [weekLoading, setWeekLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [granularity, setGranularity] = useState("daily");
  const [voucherType, setVoucherType] = useState("all");
  const { branchId, branchFilterLabel } = useSalesImportBranchFilter();
  const [topN, setTopN] = useState(10);
  const [highValueThreshold, setHighValueThreshold] = useState(50000);
  const [discountPctThreshold, setDiscountPctThreshold] = useState(40);
  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(0);
  const [report, setReport] = useState(null);
  const [voucherOptions, setVoucherOptions] = useState([]);

  const selectedWeekMeta = useMemo(
    () => uploadedWeeks.find((w) => w.periodKey === selectedPeriodKey) || null,
    [uploadedWeeks, selectedPeriodKey]
  );

  const refreshWeekList = async () => {
    const weeks = await listUploadedWeekPeriods();
    setUploadedWeeks(weeks);
    if (weeks.length && !weeks.some((w) => w.periodKey === selectedPeriodKey)) {
      setSelectedPeriodKey(weeks[0].periodKey);
    }
    return weeks;
  };

  const loadWeekRows = async (meta) => {
    if (!meta) {
      setWeekRows([]);
      return [];
    }
    setWeekLoading(true);
    try {
      const rows = await loadWeeklyLineChunkRows(meta.year, meta.monthIndex, meta.weekOfMonth);
      setWeekRows(rows);
      return rows;
    } catch (e) {
      toast.error(e?.message || "Failed to load week lineChunks.");
      setWeekRows([]);
      return [];
    } finally {
      setWeekLoading(false);
    }
  };

  const buildReport = (rows, baseLabel, weekRange = null) => {
    if (!rows.length) {
      setReport(null);
      return;
    }
    const weekFrom = weekRange?.fromDate || "";
    const weekTo = weekRange?.toDate || "";
    const manualDateFilter = Boolean(fromDate || toDate);
    const useWeekRange = reportMode === "week" && weekFrom && weekTo && !manualDateFilter;
    const reportFrom = useWeekRange ? weekFrom : manualDateFilter ? fromDate : "";
    const reportTo = useWeekRange ? weekTo : manualDateFilter ? toDate : "";
    const hasDateFilter = Boolean(reportFrom || reportTo);
    const out = generateAdvancedSalesReportFromRows(rows, {
      fromDate: reportFrom,
      toDate: reportTo,
      granularity,
      voucherType,
      branchId,
      topN: Number(topN) || 10,
      highValueThreshold: Number(highValueThreshold) || 50000,
      discountPctThreshold: Number(discountPctThreshold) || 40,
      sourceLabel: baseLabel,
    });
    if (hasDateFilter) {
      out.meta.sourceLabel = `${out.meta.matched.toLocaleString()} of ${rows.length.toLocaleString()} lines · ${baseLabel} · TranDate ${reportFrom || "…"} → ${reportTo || "…"}`;
    } else {
      out.meta.sourceLabel = `${rows.length.toLocaleString()} lines · ${baseLabel}`;
    }
    setReport(out);
    setVoucherOptions(out.voucherTypeOptions || []);
    setScanned(out.meta.scanned);
  };

  const run = async () => {
    if (reportMode === "dateRange" && fromDate && toDate && fromDate > toDate) {
      toast.error("From Date should be before To Date.");
      return;
    }
    if (reportMode === "dateRange" && !fromDate && !toDate) {
      toast.error("Set From and/or To date for date-range mode.");
      return;
    }

    setLoading(true);
    setScanned(0);
    try {
      if (reportMode === "week") {
        if (!selectedWeekMeta) {
          toast.error("Select an uploaded week.");
          return;
        }
        let rows = weekRows;
        if (!rows.length) {
          rows = await loadWeekRows(selectedWeekMeta);
        }
        if (!rows.length) {
          toast.error("No lines in this week — upload from Import Center first.");
          setReport(null);
          return;
        }
        buildReport(rows, `week ${selectedWeekMeta.periodKey}`, selectedWeekMeta);
      } else {
        const out = await generateAdvancedSalesReport({
          fromDate,
          toDate,
          granularity,
          voucherType,
          branchId,
          topN: Number(topN) || 10,
          highValueThreshold: Number(highValueThreshold) || 50000,
          discountPctThreshold: Number(discountPctThreshold) || 40,
          onProgress: (n) => setScanned(n),
        });
        setReport(out);
        setVoucherOptions(out.voucherTypeOptions || []);
        setScanned(out.meta.scanned);
      }
      toast.success("Advanced report generated.");
    } catch (e) {
      toast.error(e?.message || "Failed to generate advanced report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccessSalesDataImport(user)) return;
    refreshWeekList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!canAccessSalesDataImport(user) || reportMode !== "week") return;
    loadWeekRows(selectedWeekMeta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeekMeta?.periodKey, reportMode]);

  useEffect(() => {
    if (!canAccessSalesDataImport(user) || reportMode !== "week" || weekLoading || !weekRows.length) return;
    buildReport(weekRows, `week ${selectedWeekMeta?.periodKey || ""}`, selectedWeekMeta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekRows, voucherType, branchId, granularity, topN, highValueThreshold, discountPctThreshold, fromDate, toDate]);

  useEffect(() => {
    if (
      !canAccessSalesDataImport(user) ||
      reportMode !== "dateRange" ||
      loading ||
      weekLoading ||
      !report ||
      (!fromDate && !toDate)
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setScanned(0);
      try {
        const out = await generateAdvancedSalesReport({
          fromDate,
          toDate,
          granularity,
          voucherType,
          branchId,
          topN: Number(topN) || 10,
          highValueThreshold: Number(highValueThreshold) || 50000,
          discountPctThreshold: Number(discountPctThreshold) || 40,
          onProgress: (n) => {
            if (!cancelled) setScanned(n);
          },
        });
        if (!cancelled) {
          setReport(out);
          setVoucherOptions(out.voucherTypeOptions || []);
          setScanned(out.meta.scanned);
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.message || "Failed to refresh report for branch.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  if (!canAccessSalesDataImport(user)) {
    return <div className="p-6 text-center">Access denied — Admin or Storekeeper only.</div>;
  }

  return (
    <div className={`sdic-root${dark ? " sdic-dark" : ""}`}>
      <MenuBar />
      <header className="sdic-header">
        <div>
          <h1>Sales Advanced Reports</h1>
          <p>Trend, top entities, profitability impact, period comparison, anomaly and audit checks</p>
        </div>
        <button type="button" className="sdic-btn sdic-btn-secondary" onClick={() => setDark((d) => !d)}>
          {dark ? "Light" : "Dark"}
        </button>
      </header>

      <div className="sdic-body">
        <SalesImportNav active="advanced-reports" />

        <section className="sdic-panel">
          <h2 className="sdic-panel-title">Data source</h2>
          <p style={{ fontSize: "0.85rem", color: "var(--sdic-muted)", marginTop: 0 }}>
            Use <strong>Uploaded week</strong> (same lineChunks as View Records — all lines in the file). Date range
            mode filters by <strong>TranDate</strong> across weeks.
          </p>
          <div className="sdic-week-banner">
            <label>
              Source
              <select
                value={reportMode}
                onChange={(e) => {
                  setReportMode(e.target.value);
                  setReport(null);
                }}
                style={{ padding: "0.4rem", minWidth: "200px" }}
              >
                <option value="week">Uploaded week (recommended)</option>
                <option value="dateRange">TranDate range</option>
              </select>
            </label>
            {reportMode === "week" && (
              <label>
                Week
                <select
                  value={selectedPeriodKey}
                  onChange={(e) => setSelectedPeriodKey(e.target.value)}
                  disabled={weekLoading}
                  style={{ padding: "0.4rem", minWidth: "280px" }}
                >
                  <option value="">— Select uploaded week —</option>
                  {uploadedWeeks.map((w) => (
                    <option key={w.periodKey} value={w.periodKey}>
                      {w.periodKey} · {w.rowCount.toLocaleString()} lines
                    </option>
                  ))}
                </select>
              </label>
            )}
            {reportMode === "week" && selectedWeekMeta && (
              <span style={{ fontSize: "0.85rem", alignSelf: "center" }}>
                {weekLoading
                  ? "Loading…"
                  : `${weekRows.length.toLocaleString()} line(s) loaded`}
              </span>
            )}
          </div>
        </section>

        <section className="sdic-panel">
          <h2 className="sdic-panel-title">Filters & Options</h2>
          <div className="sdic-week-banner">
            <label>
              TranDate From
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label>
              TranDate To
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
            {reportMode === "week" && (
              <span style={{ fontSize: "0.8rem", color: "var(--sdic-muted)", alignSelf: "center" }}>
                Optional — filters loaded week by TranDate
              </span>
            )}
            <label>
              Grouping
              <select value={granularity} onChange={(e) => setGranularity(e.target.value)}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </label>
            <label>
              Voucher Type
              <select value={voucherType} onChange={(e) => setVoucherType(e.target.value)}>
                <option value="all">All Voucher Types</option>
                {voucherOptions.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Top N
              <input type="number" min={3} max={50} value={topN} onChange={(e) => setTopN(e.target.value)} />
            </label>
            <label>
              High value threshold
              <input
                type="number"
                min={0}
                value={highValueThreshold}
                onChange={(e) => setHighValueThreshold(e.target.value)}
              />
            </label>
            <label>
              High discount %
              <input
                type="number"
                min={0}
                max={100}
                value={discountPctThreshold}
                onChange={(e) => setDiscountPctThreshold(e.target.value)}
              />
            </label>
          </div>
          <div className="sdic-toolbar">
            <button
              type="button"
              className="sdic-btn sdic-btn-primary"
              onClick={run}
              disabled={loading || weekLoading}
            >
              {loading || weekLoading ? "Generating..." : "Generate Advanced Report"}
            </button>
            <span style={{ color: "var(--sdic-muted)", fontSize: "0.82rem" }}>
              {loading || weekLoading
                ? `Scanning ${scanned.toLocaleString()} records...`
                : report?.meta?.sourceLabel || ""}
            </span>
          </div>
        </section>

        {report && (
          <>
            <div className="sdic-toolbar" style={{ justifyContent: "flex-end", marginBottom: "-0.3rem" }}>
              <button
                type="button"
                className="sdic-btn sdic-btn-secondary"
                onClick={() => exportAdvancedSalesReportExcel(report, "sales-advanced-report.xlsx")}
              >
                Export Advanced Report (Excel)
              </button>
            </div>
            <div className="sdic-cards">
              <div className="sdic-card">
                <div className="sdic-card-label">Matched Records</div>
                <div className="sdic-card-value">{report.kpis.totalRecords.toLocaleString()}</div>
              </div>
              <div className="sdic-card">
                <div className="sdic-card-label">Total Sales</div>
                <div className="sdic-card-value">₹ {fm(report.kpis.totalAmount)}</div>
              </div>
              <div className="sdic-card">
                <div className="sdic-card-label">Total Qty</div>
                <div className="sdic-card-value">{fm(report.kpis.totalQty)}</div>
              </div>
              <div className="sdic-card">
                <div className="sdic-card-label">Total Discount</div>
                <div className="sdic-card-value">₹ {fm(report.kpis.totalDiscount)}</div>
              </div>
              <div className="sdic-card">
                <div className="sdic-card-label">Avg Bill Value</div>
                <div className="sdic-card-value">₹ {fm(report.kpis.avgBillValue)}</div>
              </div>
              <div className="sdic-card">
                <div className="sdic-card-label">Avg Discount %</div>
                <div className="sdic-card-value">{fm(report.kpis.avgDiscountPct)}%</div>
              </div>
            </div>

            <section className="sdic-panel">
              <h2 className="sdic-panel-title">Period Comparison</h2>
              {!report.meta.hasDateFilter ? (
                <p style={{ fontSize: "0.85rem", color: "var(--sdic-muted)" }}>
                  Period comparison is available in <strong>TranDate range</strong> mode with From/To dates set.
                  Uploaded-week mode shows totals for all {report.kpis.totalRecords.toLocaleString()} lines.
                </p>
              ) : (
                <>
                  <div className="sdic-validation-grid">
                    <div className="sdic-val-box sdic-val-valid">
                      Current Amount
                      <br />₹ {fm(report.comparison.currentPeriodAmount)}
                    </div>
                    <div className="sdic-val-box sdic-val-dup">
                      Previous Amount
                      <br />₹ {fm(report.comparison.previousPeriodAmount)}
                    </div>
                    <div className="sdic-val-box sdic-val-invalid">
                      Growth (Amount)
                      <br />
                      {pct(report.comparison.growthAmountPct)}
                    </div>
                  </div>
                  <p style={{ fontSize: "0.82rem", color: "var(--sdic-muted)" }}>
                    Record growth: {pct(report.comparison.growthRecordPct)} · Period days:{" "}
                    {report.comparison.periodDays ?? "—"}
                  </p>
                </>
              )}
            </section>

            <section className="sdic-panel">
              <h2 className="sdic-panel-title">Voucher Type Trend ({report.meta.granularity})</h2>
              <p style={{ fontSize: "0.82rem", color: "var(--sdic-muted)", marginTop: 0 }}>
                {branchFilterLabel}
                {report.meta.branchId
                  ? ` · filtered to ${salesImportBranchDisplayName(report.meta.branchId)}`
                  : ""}
                · BillAmount once per Voucher Type + Voucher No (duplicate item lines excluded)
              </p>
              <div className="sdic-table-wrap">
                <table className="sdic-table">
                  <thead>
                    <tr>
                      <th>Bucket</th>
                      <th>Vouchers</th>
                      <th>Qty</th>
                      <th>Bill Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const maxAmt = (report.trend || []).reduce((m, x) => Math.max(m, Number(x.amount) || 0), 0) || 0;
                      return report.trend.map((r) => (
                        <tr key={r.key}>
                          <td>{r.key}</td>
                          <td>{r.records.toLocaleString()}</td>
                          <td>{fm(r.qty)}</td>
                          <td>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                              <div>₹ {fm(r.amount)}</div>
                              <div className="sdic-bar">
                                <div
                                  className="sdic-bar-fill"
                                  style={{
                                    width: `${maxAmt ? Math.max(0, (Number(r.amount) || 0) / maxAmt) * 100 : 0}%`,
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="sdic-panel">
              <h2 className="sdic-panel-title">Voucher Type Breakdown</h2>
              <p style={{ fontSize: "0.82rem", color: "var(--sdic-muted)", marginTop: 0 }}>
                {branchFilterLabel} · {report.meta.sourceLabel} · BillAmount per unique Voucher Type + Voucher No
                · voucher counts should sum to {(report.meta.uniqueVoucherCount ?? 0).toLocaleString()} (
                {report.kpis.totalRecords.toLocaleString()} import lines before dedupe)
              </p>
              <div className="sdic-table-wrap">
                <table className="sdic-table">
                  <thead>
                    <tr>
                      <th>Voucher Type</th>
                      <th>Vouchers</th>
                      <th>Qty</th>
                      <th>Bill Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const maxAmt =
                        (report.voucherBreakdown || []).reduce((m, x) => Math.max(m, Number(x.amount) || 0), 0) || 0;
                      return report.voucherBreakdown.map((r) => (
                        <tr key={r.key}>
                          <td>{r.key}</td>
                          <td>{r.records.toLocaleString()}</td>
                          <td>{fm(r.qty)}</td>
                          <td>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                              <div>₹ {fm(r.amount)}</div>
                              <div className="sdic-bar">
                                <div
                                  className="sdic-bar-fill"
                                  style={{
                                    width: `${maxAmt ? Math.max(0, (Number(r.amount) || 0) / maxAmt) * 100 : 0}%`,
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="sdic-panel">
              <h2 className="sdic-panel-title">Top Customers / Items / Employees</h2>
              <div className="sdic-validation-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                <div>
                  <h3 className="sdic-panel-title" style={{ fontSize: "0.9rem" }}>
                    Top Customers
                  </h3>
                  <div className="sdic-table-wrap">
                    <table className="sdic-table">
                      <tbody>
                        {report.topCustomers.map((r) => (
                          <tr key={r.key}>
                            <td>{r.key}</td>
                            <td>₹ {fm(r.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <h3 className="sdic-panel-title" style={{ fontSize: "0.9rem" }}>
                    Top Items
                  </h3>
                  <div className="sdic-table-wrap">
                    <table className="sdic-table">
                      <tbody>
                        {report.topItems.map((r) => (
                          <tr key={r.key}>
                            <td>{r.key}</td>
                            <td>₹ {fm(r.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <h3 className="sdic-panel-title" style={{ fontSize: "0.9rem" }}>
                    Top Employees
                  </h3>
                  <div className="sdic-table-wrap">
                    <table className="sdic-table">
                      <tbody>
                        {report.topEmployees.map((r) => (
                          <tr key={r.key}>
                            <td>{r.key}</td>
                            <td>₹ {fm(r.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </section>

            <section className="sdic-panel">
              <h2 className="sdic-panel-title">Anomaly & Audit Checks</h2>
              <div className="sdic-validation-grid">
                <div className="sdic-val-box sdic-val-dup">
                  High Value
                  <br />
                  {report.anomalies.highValueCount.toLocaleString()}
                </div>
                <div className="sdic-val-box sdic-val-dup">
                  High Discount
                  <br />
                  {report.anomalies.highDiscountCount.toLocaleString()}
                </div>
                <div className="sdic-val-box sdic-val-invalid">
                  Non-positive Amount
                  <br />
                  {report.anomalies.negativeOrZeroAmount.toLocaleString()}
                </div>
                <div className="sdic-val-box sdic-val-invalid">
                  Zero Qty
                  <br />
                  {report.anomalies.zeroQtyCount.toLocaleString()}
                </div>
                <div className="sdic-val-box sdic-val-invalid">
                  Invalid Date
                  <br />
                  {report.audit.invalidDateCount.toLocaleString()}
                </div>
                <div className="sdic-val-box sdic-val-invalid">
                  Missing Bill/Product
                  <br />
                  {report.audit.missingIdentity.toLocaleString()}
                </div>
              </div>
              <p style={{ fontSize: "0.82rem", color: "var(--sdic-muted)" }}>
                Duplicate invoice item keys detected in scan: {report.audit.duplicateInvoiceItem.toLocaleString()}
              </p>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
